import React, { useState } from 'react';
import clsx from 'clsx';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SVGGoogleLogo from '@/components/_new/svgs/SVGGoogleLogo';
import CreatorCardModal from '@/components/CreatorCardModal/CreatorCardModal';
import { RecommenderAvatarImage } from '@/components/shared/RecommenderAvatarImage/RecommenderAvatarImage';
import type { RecommenderAvatar } from '@/utils/locationDataExtractor';

const MAX_AVATARS = 6;

/** Formats a review count for compact display (e.g. 1100 → "1.1k", 900 → "900"). */
function formatReviewCount(count: number): string {
  if (count >= 1000) {
    return `${parseFloat((count / 1000).toFixed(1))}k`;
  }
  return count.toLocaleString();
}

interface PlaceModalGoogleRatingProps {
  rating: number;
  userRatingCount: number | null;
  mapsUrl: string | null;
  className?: string;
}

/** Google rating box linking to Maps. Mobile: Figma "Google reviews", beside the phone row. */
export const PlaceModalGoogleRating: React.FC<PlaceModalGoogleRatingProps> = ({
  rating,
  userRatingCount,
  mapsUrl,
  className,
}) => {
  /** Figma: ~20px-tall wordmark; natural asset is 74×24. */
  const googleLogoWidth = Math.round((20 * 74) / 24);
  return (
    <a
      href={mapsUrl ?? undefined}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Google rating — view on Google Maps (opens in a new tab)"
      className={clsx(
        'flex flex-col gap-2 rounded-[10px] border-[1.5px] border-solid border-[#D9D9D9] py-3 pl-[15px] pr-3 no-underline',
        'max-md:shrink-0 max-md:rounded-xl max-md:border max-md:border-[#E1E1E7] max-md:px-4 max-md:pb-4',
        mapsUrl ? 'transition-colors hover:bg-[#FAFAFA]' : 'pointer-events-none',
        className,
      )}
    >
      <span className="flex items-center gap-2 text-[#525257]">
        <SVGGoogleLogo width={googleLogoWidth} />
        <OpenInNewIcon sx={{ fontSize: 20, '@media (max-width: 767px)': { fontSize: 16 } }} aria-hidden />
      </span>
      <span className="flex items-center gap-1 whitespace-nowrap font-manrope text-sm leading-5 tracking-[0.02em]">
        <span className="font-semibold text-[#252528] max-md:font-bold max-md:text-black">{rating.toFixed(1)}/5</span>
        {userRatingCount != null && (
          <span className="font-normal text-[#525257]">{formatReviewCount(userRatingCount)} reviews</span>
        )}
      </span>
    </a>
  );
};

interface PlaceModalRecommendersProps {
  explicitRecommendations: number;
  recommendationAvatars: RecommenderAvatar[];
  /** Hidden on mobile when Community Tips follow right below. */
  onReadTipsClick?: () => void;
  className?: string;
}

/** "Recommended by N Globe Thrivers" facepile; avatars open the creator card. */
export const PlaceModalRecommenders: React.FC<PlaceModalRecommendersProps> = ({
  explicitRecommendations: explicitRecCount,
  recommendationAvatars,
  onReadTipsClick,
  className,
}) => {
  const [creatorCardUserId, setCreatorCardUserId] = useState<string | null>(null);

  // Every avatar is an explicit recommender, so the heading never counts fewer than it shows.
  const explicitRecommendations = Math.max(explicitRecCount, recommendationAvatars.length);
  if (explicitRecommendations <= 0) return null;

  /** Pad with placeholders when we have a rec count but fewer avatar URLs. */
  const avatarSlotCount = Math.min(
    MAX_AVATARS,
    Math.max(explicitRecommendations, recommendationAvatars.length),
  );
  const avatarSlots = Array.from({ length: avatarSlotCount }, (_, i) => recommendationAvatars[i]);
  const othersCount = explicitRecommendations - avatarSlotCount;

  return (
    <section className={clsx('flex min-w-0 flex-col gap-3 px-2.5 pb-5 pt-3 max-md:px-0 max-md:pb-0 max-md:pt-1', className)}>
      <div className="flex flex-col gap-2 max-md:gap-2.5">
        <p className="m-0 font-manrope text-xs font-semibold leading-4 tracking-[0.02em] text-[#525257]">
          Recommended by {explicitRecommendations} Globe Thriver{explicitRecommendations !== 1 ? 's' : ''}
        </p>
        {avatarSlotCount > 0 && (
          <div className="flex items-center gap-2 max-md:gap-4">
            <div className="flex items-center">
              {avatarSlots.map((avatar, i) => {
                const userId = avatar?.userId;
                return (
                  <RecommenderAvatarImage
                    key={avatar?.avatarUrl ?? `placeholder-${i}`}
                    url={avatar?.avatarUrl}
                    className={clsx(
                      '-ml-3 h-[30px] w-[30px] rounded-full border-2 border-white object-cover first:ml-0',
                      userId && 'cursor-pointer',
                    )}
                    onClick={userId ? () => setCreatorCardUserId(userId) : undefined}
                  />
                );
              })}
            </div>
            {othersCount > 0 && (
              <span className="px-2 font-manrope text-sm font-semibold leading-[17px] text-[#525257] max-md:px-0 max-md:text-xs max-md:leading-4 max-md:tracking-[0.02em]">
                +{othersCount} others
              </span>
            )}
          </div>
        )}
      </div>
      {onReadTipsClick && (
        <button
          type="button"
          onClick={onReadTipsClick}
          className="self-start border-none bg-transparent p-0 text-left font-manrope text-xs font-semibold leading-4 tracking-[0.02em] text-[#FF5212] underline"
        >
          Click here to read the community tips
        </button>
      )}

      {creatorCardUserId && (
        <CreatorCardModal
          isOpen
          onClose={() => setCreatorCardUserId(null)}
          userId={creatorCardUserId}
          initialData={{
            avatar: recommendationAvatars.find((a) => a.userId === creatorCardUserId)?.avatarUrl,
          }}
        />
      )}
    </section>
  );
};

interface PlaceModalSocialProofProps {
  explicitRecommendations: number;
  recommendationAvatars: RecommenderAvatar[];
  googleRating: number | null;
  googleUserRatingCount: number | null;
  googleMapsUrl: string | null;
  onReadTipsClick: () => void;
}

/** Figma "Social proof and Google reviews" (desktop): recommender facepile + Google rating box. */
const PlaceModalSocialProof: React.FC<PlaceModalSocialProofProps> = ({
  explicitRecommendations,
  recommendationAvatars,
  googleRating,
  googleUserRatingCount,
  googleMapsUrl,
  onReadTipsClick,
}) => {
  const hasRecommendations = explicitRecommendations > 0 || recommendationAvatars.length > 0;
  if (!hasRecommendations && googleRating == null) return null;

  return (
    // Two columns like the details row above: recommenders left, Google box always in the right column
    <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-2">
      <PlaceModalRecommenders
        explicitRecommendations={explicitRecommendations}
        recommendationAvatars={recommendationAvatars}
        onReadTipsClick={onReadTipsClick}
      />
      {googleRating != null && (
        <PlaceModalGoogleRating
          rating={googleRating}
          userRatingCount={googleUserRatingCount}
          mapsUrl={googleMapsUrl}
          className="justify-self-start md:col-start-2"
        />
      )}
    </div>
  );
};

export default PlaceModalSocialProof;
